/**
 * Middleware: adapta la respuesta de `GET …/matches/{matchId}/live-events`
 * al formato `lineups` del detalle de partido (p. ej. `matchDetailsMock.json`).
 *
 * El feed es un stream de eventos; se aplica sobre `previousLineups` (carga inicial `view=all`).
 */

import {
  buildStreamTeamIdToCardTeamIdMap,
  TEAM_SIDE_LABELS,
} from './matchTeamRefUtils';
import { normalizeStreamEventsList } from './normalizeLiveEventsStream';

const LINEUP_PLAYER_DETAIL_TYPES = new Set([
  'GOAL',
  'YELLOW_CARD',
  'RED_CARD',
  'RED_2Y',
  'SECOND_YELLOW',
  'SECOND_YELLOW_RED_CARD',
  'SUBSTITUTION_IN',
  'SUBSTITUTION_OUT',
]);

const SECOND_YELLOW_TYPES = new Set([
  'RED_2Y',
  'SECOND_YELLOW',
  'SECOND_YELLOW_RED_CARD',
]);

const SKIPPED_ACTION_TYPES = new Set(['SHOT_BLOCKED_BY']);

const POSITION_NUMBER_TO_CODE = {
  GOALKEEPER: 'GK',
  DEFENDER: 'DF',
  MIDFIELDER: 'MF',
  FORWARD: 'FW',
};

const CARD_ISSUED_TYPE_MAP = {
  YELLOW: { type: 'YELLOW_CARD', name: 'Tarjeta amarilla' },
  RED: { type: 'RED_CARD', name: 'Tarjeta roja' },
  RED_CARD: { type: 'RED_CARD', name: 'Tarjeta roja' },
  SECOND_YELLOW: { type: 'SECOND_YELLOW_RED_CARD', name: 'Tarjeta amarilla' },
  SECOND_YELLOW_RED: { type: 'SECOND_YELLOW_RED_CARD', name: 'Tarjeta amarilla' },
  RED_SECOND_YELLOW: {
    type: 'SECOND_YELLOW_RED_CARD',
    name: 'Tarjeta amarilla',
  },
};

/** Baseline de `view=all` por partido; solo para replay completo del stream (no polls incrementales). */
const mergeBaselineByMatchId = new Map();

/** @param {string|null|undefined} matchId */
export function resetLiveEventsLineupsMergeBaseline(matchId) {
  if (matchId != null && String(matchId).trim() !== '') {
    mergeBaselineByMatchId.delete(String(matchId));
    return;
  }
  mergeBaselineByMatchId.clear();
}

const DETAIL_DEDUPE_BY_MINUTE_TYPES = new Set([
  'GOAL',
  'YELLOW_CARD',
  'RED_CARD',
  'SECOND_YELLOW_RED_CARD',
  'SUBSTITUTION_IN',
  'SUBSTITUTION_OUT',
]);

function cloneLineups(lineups) {
  return JSON.parse(JSON.stringify(lineups));
}

function normalizeDetailsList(details) {
  if (Array.isArray(details)) return details.filter(Boolean);
  if (details && typeof details === 'object') return [details];
  return [];
}

function detailDedupeKey(detail) {
  const type = detail?.type != null ? String(detail.type) : '';
  const minute = detail?.minute != null ? String(detail.minute) : '';
  const dedupeType = type === 'OWN_GOAL' ? 'GOAL' : type;
  if (DETAIL_DEDUPE_BY_MINUTE_TYPES.has(dedupeType)) {
    return `${dedupeType}|${minute}`;
  }
  const eventId = detail?.eventId != null ? String(detail.eventId) : '';
  if (eventId) return `${type}|${minute}|${eventId}`;
  return `${type}|${minute}`;
}

function appendPlayerMatchDetail(player, detail) {
  if (!player || !detail?.type) return;
  const list = normalizeDetailsList(player.playerMatchDetails);
  const key = detailDedupeKey(detail);
  if (list.some((d) => detailDedupeKey(d) === key)) return;
  list.push(detail);
  player.playerMatchDetails = list;
}

function mapPositionCode(positionNumber) {
  if (positionNumber == null) return 'MF';
  const raw = String(positionNumber).trim().toUpperCase();
  if (POSITION_NUMBER_TO_CODE[raw]) return POSITION_NUMBER_TO_CODE[raw];
  if (['GK', 'DF', 'MF', 'FW'].includes(raw)) return raw;
  return 'MF';
}

function mapStreamSquadPlayer(streamPlayer, previousPlayer) {
  const player = {
    playerId:
      streamPlayer?.playerId != null ? String(streamPlayer.playerId) : undefined,
    name:
      streamPlayer?.playerName != null
        ? String(streamPlayer.playerName)
        : streamPlayer?.name != null
          ? String(streamPlayer.name)
          : '',
    number:
      streamPlayer?.uniformNumber != null
        ? Number(streamPlayer.uniformNumber)
        : streamPlayer?.number != null
          ? Number(streamPlayer.number)
          : undefined,
    position: mapPositionCode(
      streamPlayer?.positionNumber ?? streamPlayer?.position,
    ),
    playerMatchDetails: normalizeDetailsList(previousPlayer?.playerMatchDetails),
  };

  if (previousPlayer?.photo) player.photo = previousPlayer.photo;
  if (previousPlayer?.image) player.image = previousPlayer.image;

  return player;
}

function buildPlayerLookupMaps(lineup) {
  const byId = new Map();
  const byName = new Map();
  [...(lineup?.startingXI ?? []), ...(lineup?.substitutes ?? [])].forEach((p) => {
    if (p?.playerId != null) byId.set(String(p.playerId), p);
    const normalized = normalizePlayerName(p?.name);
    if (normalized) byName.set(normalized, p);
  });
  return { byId, byName };
}

function resolvePreviousPlayer(streamPlayer, lookup) {
  const id =
    streamPlayer?.playerId != null ? String(streamPlayer.playerId) : null;
  if (id && lookup.byId.has(id)) return lookup.byId.get(id);

  const normalized = normalizePlayerName(
    streamPlayer?.playerName ?? streamPlayer?.name,
  );
  if (normalized && lookup.byName.has(normalized)) {
    return lookup.byName.get(normalized);
  }
  return null;
}

function getMergeBaseline(matchId, previousLineups) {
  if (!matchId || !Array.isArray(previousLineups) || previousLineups.length === 0) {
    return previousLineups;
  }
  const key = String(matchId);
  if (!mergeBaselineByMatchId.has(key)) {
    mergeBaselineByMatchId.set(key, cloneLineups(previousLineups));
  }
  return mergeBaselineByMatchId.get(key);
}

function resolveLineupLookupTeamId(streamTeamId, context) {
  if (streamTeamId == null) return null;
  const sid = String(streamTeamId).trim();
  if (TEAM_SIDE_LABELS.has(sid)) {
    return resolveTeamId({
      teamLabel: sid,
      teamId: null,
      homeTeamId: context.homeTeamId,
      awayTeamId: context.awayTeamId,
    });
  }
  const mapped = context.streamTeamIdMap?.get(sid);
  if (mapped) return mapped;
  return sid;
}

function findLineupForContext(lineups, streamTeamId, context) {
  const lookupId = resolveLineupLookupTeamId(streamTeamId, context);
  return findLineupForStreamTeam(
    lineups,
    lookupId,
    context.homeTeamId,
    context.awayTeamId,
  );
}

/**
 * Resuelve el bloque de alineación para un `teamId` del stream o etiqueta HOME/AWAY.
 * Si los `teamId` del baseline (MEX/POR) no coinciden con los del `matchCard` (GN*),
 * asigna por posición local [0] / visita [1].
 */
function findLineupForStreamTeam(lineups, streamTeamId, homeTeamId, awayTeamId) {
  if (!Array.isArray(lineups) || lineups.length === 0 || streamTeamId == null) {
    return null;
  }

  const sid = String(streamTeamId).trim();
  const direct = lineups.find((l) => String(l?.teamId).trim() === sid);
  if (direct) return direct;

  const homeId = homeTeamId != null ? String(homeTeamId).trim() : null;
  const awayId = awayTeamId != null ? String(awayTeamId).trim() : null;

  if (homeId && sid === homeId) {
    const byHome = lineups.find((l) => String(l?.teamId).trim() === homeId);
    return byHome ?? lineups[0] ?? null;
  }

  if (awayId && sid === awayId) {
    const byAway = lineups.find((l) => String(l?.teamId).trim() === awayId);
    if (byAway) return byAway;
    if (lineups.length >= 2) {
      const homeLineup = homeId
        ? lineups.find((l) => String(l?.teamId).trim() === homeId)
        : lineups[0];
      return lineups.find((l) => l !== homeLineup) ?? lineups[1] ?? null;
    }
    return lineups[0] ?? null;
  }

  return null;
}

/** Alinea `teamId` de cada bloque con los del `matchCard` para que el stream los encuentre. */
function alignLineupTeamIds(lineups, homeTeamId, awayTeamId) {
  if (!Array.isArray(lineups) || lineups.length === 0) return [];
  if (!homeTeamId && !awayTeamId) return lineups;

  const copy = lineups.map((l) => ({ ...l }));

  if (copy.length === 1) {
    if (homeTeamId) copy[0].teamId = String(homeTeamId);
    else if (awayTeamId) copy[0].teamId = String(awayTeamId);
    return copy;
  }

  const homeId = homeTeamId != null ? String(homeTeamId).trim() : null;
  const awayId = awayTeamId != null ? String(awayTeamId).trim() : null;

  let homeLineup = homeId
    ? copy.find((l) => String(l?.teamId).trim() === homeId)
    : null;
  if (!homeLineup) homeLineup = copy[0];

  let awayLineup = awayId
    ? copy.find(
        (l) => l !== homeLineup && String(l?.teamId).trim() === awayId,
      )
    : null;
  if (!awayLineup) {
    awayLineup = copy.find((l) => l !== homeLineup) ?? copy[1] ?? copy[0];
  }

  if (homeId && homeLineup) homeLineup.teamId = homeId;
  if (awayId && awayLineup) awayLineup.teamId = awayId;

  const ordered = [];
  if (homeLineup) ordered.push(homeLineup);
  if (awayLineup && awayLineup !== homeLineup) ordered.push(awayLineup);
  copy.forEach((l) => {
    if (!ordered.includes(l)) ordered.push(l);
  });
  return ordered;
}

/**
 * Normaliza `lineups` a un arreglo plano de dos equipos (local / visita).
 * Soporta `[{ teamId, startingXI }, …]` y `[{ homeTeam, awayTeam }]`.
 */
function normalizeLineupsArray(lineups, homeTeamId, awayTeamId, matchId) {
  if (!Array.isArray(lineups) || lineups.length === 0) return [];

  const first = lineups[0];
  if (first?.homeTeam || first?.awayTeam) {
    const homeBundle = Array.isArray(first.homeTeam)
      ? first.homeTeam[0]
      : first.homeTeam;
    const awayBundle = Array.isArray(first.awayTeam)
      ? first.awayTeam[0]
      : first.awayTeam;

    const flat = [];
    if (homeBundle) {
      flat.push({
        ...homeBundle,
        matchId: homeBundle.matchId ?? matchId,
        teamId: homeTeamId ?? homeBundle.teamId,
      });
    }
    if (awayBundle) {
      flat.push({
        ...awayBundle,
        matchId: awayBundle.matchId ?? matchId,
        teamId: awayTeamId ?? awayBundle.teamId,
      });
    }
    return alignLineupTeamIds(
      flat.filter((l) => l?.teamId != null),
      homeTeamId,
      awayTeamId,
    );
  }

  return alignLineupTeamIds(
    lineups
      .filter((l) => l && (l.teamId != null || l.startingXI != null))
      .map((l) => ({ ...l, matchId: l.matchId ?? matchId })),
    homeTeamId,
    awayTeamId,
  );
}

function normalizePlayerName(name) {
  if (name == null) return '';
  return String(name)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Actualiza `playerId` (y nombre si viene del stream) tras enlazar con el feed. */
function syncPlayerIdentifiers(player, { playerId, name }) {
  if (!player) return;

  const streamId = playerId != null ? String(playerId).trim() : '';
  const streamName = name != null ? String(name).trim() : '';

  if (streamId) player.playerId = streamId;
  if (streamName) player.name = streamName;
}

function resolveParticipantNumber(participant) {
  const raw =
    participant?.uniformNumber ??
    participant?.number ??
    participant?.playerNumber;
  if (raw == null || String(raw).trim() === '') return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

function findPlayerInTeamLineup(lineup, { playerId, name, number }) {
  if (!lineup) return null;

  const pools = [
    ...(lineup.startingXI ?? []).map((p, index) => ({ p, listKey: 'startingXI', index })),
    ...(lineup.substitutes ?? []).map((p, index) => ({
      p,
      listKey: 'substitutes',
      index,
    })),
  ];

  if (playerId != null && String(playerId).trim() !== '') {
    const id = String(playerId).trim();
    const hit = pools.find(({ p }) => String(p?.playerId).trim() === id);
    if (hit) {
      syncPlayerIdentifiers(hit.p, { playerId, name });
      return hit;
    }
  }

  const normalizedName =
    name != null && String(name).trim() !== ''
      ? normalizePlayerName(name)
      : null;
  const jerseyNumber =
    number != null ? Number(number) : null;
  const hasJersey =
    jerseyNumber != null && !Number.isNaN(jerseyNumber);

  if (normalizedName) {
    const nameHits = pools.filter(
      ({ p }) => normalizePlayerName(p?.name) === normalizedName,
    );
    const hit =
      hasJersey && nameHits.length > 1
        ? nameHits.find(({ p }) => Number(p?.number) === jerseyNumber)
        : nameHits[0];
    if (hit) {
      syncPlayerIdentifiers(hit.p, { playerId, name });
      return hit;
    }
  }

  if (hasJersey) {
    const hit = pools.find(({ p }) => Number(p?.number) === jerseyNumber);
    if (hit) {
      syncPlayerIdentifiers(hit.p, { playerId, name });
      return hit;
    }
  }

  return null;
}

function lookupPlayerInLineups(lineups, streamTeamId, identifiers, context) {
  const ref = findPlayerInLineups(lineups, identifiers, streamTeamId, context);
  return ref.player;
}

function findPlayerInLineups(lineups, identifiers, streamTeamId, context) {
  const ids =
    identifiers != null && typeof identifiers === 'object'
      ? identifiers
      : { playerId: identifiers };

  if (ids.playerId == null && !ids.name) {
    return { lineup: null, player: null, listKey: null, index: -1 };
  }

  if (streamTeamId == null) {
    return { lineup: null, player: null, listKey: null, index: -1 };
  }

  const lineup = findLineupForContext(lineups, streamTeamId, context);
  const hit = findPlayerInTeamLineup(lineup, ids);
  if (!hit) {
    return { lineup: null, player: null, listKey: null, index: -1 };
  }

  return {
    lineup,
    player: hit.p,
    listKey: hit.listKey,
    index: hit.index,
  };
}

function resolveTeamId({ teamLabel, teamId, homeTeamId, awayTeamId }) {
  const label =
    teamLabel ??
    (teamId != null && TEAM_SIDE_LABELS.has(String(teamId).trim())
      ? String(teamId).trim()
      : null);

  if (label === 'HOME_TEAM' && homeTeamId) return String(homeTeamId);
  if (label === 'AWAY_TEAM' && awayTeamId) return String(awayTeamId);

  if (teamId != null && !TEAM_SIDE_LABELS.has(String(teamId).trim())) {
    return String(teamId).trim();
  }

  return null;
}

function mapParticipantToRelatedPlayer(payload, prefix = 'relatedPlayer') {
  const id = payload?.[`${prefix}Id`];
  const name = payload?.[`${prefix}Name`];
  const number = payload?.[`${prefix}Number`];
  const position = payload?.[`${prefix}Position`];

  if (id == null && (name == null || String(name).trim() === '')) return null;

  const related = {};
  if (id != null) related.playerId = String(id);
  if (name != null) related.name = String(name);
  if (number != null && number !== '') related.number = Number(number);
  if (position != null) {
    related.position = mapPositionCode(position);
  }
  return related;
}

function mapActionDetailName(type, displayName, rawType) {
  const label = displayName != null ? String(displayName).trim() : '';
  if (type === 'OWN_GOAL') {
    return label || 'Gol en contra';
  }
  if (type === 'GOAL') {
    if (/penal/i.test(label)) return 'Penal';
  }
  if (type === 'YELLOW_CARD') return 'Tarjeta amarilla';
  if (type === 'RED_CARD') return 'Tarjeta roja';
  if (SECOND_YELLOW_TYPES.has(type)) return 'Tarjeta amarilla';
  if (type === 'SUBSTITUTION_OUT') return 'Sale';
  if (type === 'SUBSTITUTION_IN') return 'Entra';
  return label || type;
}

function mapActionTypeForLineup(rawType) {
  if (!rawType) return null;
  if (SKIPPED_ACTION_TYPES.has(rawType)) return null;
  if (rawType === 'OWN_GOAL') return 'OWN_GOAL';
  if (SECOND_YELLOW_TYPES.has(rawType)) return 'SECOND_YELLOW_RED_CARD';
  if (LINEUP_PLAYER_DETAIL_TYPES.has(rawType)) return rawType;
  return null;
}

function buildDetailFromAction(payload, streamEvent) {
  const rawType = payload?.type != null ? String(payload.type).trim() : '';
  const type = mapActionTypeForLineup(rawType);
  if (!type) return null;

  const detail = {
    eventId:
      payload?.actionId != null
        ? String(payload.actionId)
        : streamEvent?.eventId != null
          ? String(streamEvent.eventId)
          : undefined,
    type,
    name: mapActionDetailName(type, payload?.displayName, rawType),
    minute: payload?.minute != null ? Number(payload.minute) : undefined,
  };

  const related = mapParticipantToRelatedPlayer(payload, 'relatedPlayer');
  if (related) detail.relatedPlayer = related;

  return detail;
}

function applyLineupConfirmed(lineups, payload, teamId, context) {
  const current = payload?.currentLineup;
  if (!current) return;

  const lineup = findLineupForContext(lineups, teamId, context);
  if (!lineup) return;

  const resolvedTeamId = resolveLineupLookupTeamId(teamId, context);
  if (resolvedTeamId) lineup.teamId = String(resolvedTeamId);
  const previousLookup = buildPlayerLookupMaps(lineup);

  if (current.formation != null) {
    lineup.formation = String(current.formation);
  }

  const starters = (Array.isArray(current.starters) ? current.starters : [])
    .filter((p) => p?.isActive !== false)
    .map((p) => mapStreamSquadPlayer(p, resolvePreviousPlayer(p, previousLookup)));

  const benchPlayers = [
    ...(Array.isArray(current.starters) ? current.starters : []).filter(
      (p) => p?.isActive === false,
    ),
    ...(Array.isArray(current.bench) ? current.bench : []),
  ].map((p) => mapStreamSquadPlayer(p, resolvePreviousPlayer(p, previousLookup)));

  if (starters.length > 0) lineup.startingXI = starters;
  lineup.substitutes = benchPlayers;
}

function hasSubstitutionParticipantRef(participant) {
  if (!participant) return false;
  if (participant.playerId != null && String(participant.playerId).trim() !== '') {
    return true;
  }
  const name = participant.playerName ?? participant.name;
  return name != null && String(name).trim() !== '';
}

function mapSubstitutionParticipantIdentifiers(participant) {
  return {
    playerId: participant?.playerId,
    name: participant?.playerName ?? participant?.name,
    number: resolveParticipantNumber(participant),
  };
}

/**
 * Aplica sustitución como en `view=all` / WCAlineaciones: solo `playerMatchDetails`
 * (SUBSTITUTION_OUT en titular, SUBSTITUTION_IN en suplente), sin mover arrays.
 */
function applySubstitutionMade(lineups, payload, teamId, context) {
  const out = payload?.playerOut;
  const inn = payload?.playerIn;
  if (!hasSubstitutionParticipantRef(out) || !hasSubstitutionParticipantRef(inn)) {
    return;
  }

  const minute = payload?.minute != null ? Number(payload.minute) : undefined;
  const lineup = findLineupForContext(lineups, teamId, context);
  if (!lineup) return;

  const outIds = mapSubstitutionParticipantIdentifiers(out);
  const inIds = mapSubstitutionParticipantIdentifiers(inn);

  const outRef = findPlayerInLineups(lineups, outIds, teamId, context);
  const inRef = findPlayerInLineups(lineups, inIds, teamId, context);
  if (!outRef.player || !inRef.player || outRef.lineup !== lineup) return;

  const relatedIn = {
    playerId: inRef.player.playerId,
    name: inRef.player.name,
    number: inRef.player.number,
    position: inRef.player.position,
  };
  const relatedOut = {
    playerId: outRef.player.playerId,
    name: outRef.player.name,
    number: outRef.player.number,
    position: outRef.player.position,
  };

  appendPlayerMatchDetail(outRef.player, {
    type: 'SUBSTITUTION_OUT',
    name: 'Sale',
    minute,
    relatedPlayer: relatedIn,
  });
  appendPlayerMatchDetail(inRef.player, {
    type: 'SUBSTITUTION_IN',
    name: 'Entra',
    minute,
    relatedPlayer: relatedOut,
  });
}

function applyActionRecorded(lineups, streamEvent, context) {
  const payload = streamEvent?.payload;
  if (!payload) return;

  const detail = buildDetailFromAction(payload, streamEvent);
  if (!detail) return;

  if (detail.type === 'SUBSTITUTION_IN' || detail.type === 'SUBSTITUTION_OUT') {
    return;
  }

  const teamId = resolveTeamId({
    teamLabel: payload.team,
    teamId: payload.teamId,
    homeTeamId: context.homeTeamId,
    awayTeamId: context.awayTeamId,
  });
  if (!teamId) return;

  const playerId = payload.participantId ?? payload.participant?.playerId;
  const player = lookupPlayerInLineups(
    lineups,
    teamId,
    {
      playerId,
      name: payload.participantName,
    },
    context,
  );
  if (!player) return;

  appendPlayerMatchDetail(player, detail);
}

function applyCardIssued(lineups, payload, context) {
  const cardType = payload?.cardType != null ? String(payload.cardType).trim() : '';
  const mapped = CARD_ISSUED_TYPE_MAP[cardType];
  if (!mapped) return;

  const teamId = resolveTeamId({
    teamLabel: payload?.team,
    teamId: payload?.teamId,
    homeTeamId: context.homeTeamId,
    awayTeamId: context.awayTeamId,
  });
  if (!teamId) return;

  const offended = payload?.playerOffended;
  const player = lookupPlayerInLineups(
    lineups,
    teamId,
    {
      playerId: offended?.playerId ?? payload?.playerId,
      name:
        offended?.playerName ??
        payload?.playerName ??
        payload?.participantName,
    },
    context,
  );
  if (!player) return;

  appendPlayerMatchDetail(player, {
    type: mapped.type,
    name: mapped.name,
    minute: payload?.minute != null ? Number(payload.minute) : undefined,
  });
}

function lookupScorerPlayer(lineups, payload, context) {
  const scorer = payload?.scorer ?? {};
  const scorerId = scorer.playerId ?? payload?.playerId;
  const scorerName =
    scorer.playerName ??
    scorer.name ??
    payload?.playerName ??
    payload?.scorerName;

  if (
    (scorerId == null || String(scorerId).trim() === '') &&
    (scorerName == null || String(scorerName).trim() === '')
  ) {
    return null;
  }

  const identifiers = { playerId: scorerId, name: scorerName };

  if (payload?.ownGoal) {
    for (const teamId of [context.homeTeamId, context.awayTeamId]) {
      if (teamId == null) continue;
      const player = lookupPlayerInLineups(
        lineups,
        teamId,
        identifiers,
        context,
      );
      if (player) return player;
    }
    return null;
  }

  const teamId = resolveTeamId({
    teamLabel: payload?.scoringTeam,
    teamId:
      payload?.scoringTeamId ??
      payload?.scorerTeamId ??
      payload?.teamId,
    homeTeamId: context.homeTeamId,
    awayTeamId: context.awayTeamId,
  });
  if (!teamId) return null;

  return lookupPlayerInLineups(lineups, teamId, identifiers, context);
}

function applyScoreUpdated(lineups, payload, context) {
  const player = lookupScorerPlayer(lineups, payload, context);
  if (!player) return;

  const name = payload?.penalty ? 'Penal' : payload?.ownGoal ? 'Gol en contra' : 'Gol';

  const detail = {
    type: payload?.ownGoal ? 'OWN_GOAL' : 'GOAL',
    name,
    minute: payload?.minute != null ? Number(payload.minute) : undefined,
  };

  if (payload?.assistedBy != null && String(payload.assistedBy).trim() !== '') {
    detail.relatedPlayer = { name: String(payload.assistedBy).trim() };
  } else {
    const related = mapParticipantToRelatedPlayer(payload, 'relatedPlayer');
    if (related) detail.relatedPlayer = related;
  }

  appendPlayerMatchDetail(player, detail);
}

function applyStructuralStreamEvent(lineups, streamEvent, context) {
  const eventType = streamEvent?.eventType;
  const payload = streamEvent?.payload;

  if (eventType === 'lineup.confirmed' && payload) {
    const teamId = resolveTeamId({
      teamLabel: payload.team,
      teamId: payload.teamId,
      homeTeamId: context.homeTeamId,
      awayTeamId: context.awayTeamId,
    });
    if (teamId) {
      applyLineupConfirmed(lineups, payload, teamId, context);
    }
    return;
  }

  if (eventType === 'substitution.made' && payload) {
    const teamId = resolveTeamId({
      teamLabel: payload.team,
      teamId:
        payload.teamId ??
        payload.substitutionTeamId ??
        payload.playerOut?.teamId,
      homeTeamId: context.homeTeamId,
      awayTeamId: context.awayTeamId,
    });
    if (teamId) {
      applySubstitutionMade(lineups, payload, teamId, context);
    }
  }
}

function applyDetailStreamEvent(lineups, streamEvent, context) {
  const eventType = streamEvent?.eventType;
  const payload = streamEvent?.payload;

  if (eventType === 'action.recorded') {
    applyActionRecorded(lineups, streamEvent, context);
    return;
  }

  if (eventType === 'card.issued') {
    applyCardIssued(lineups, payload, context);
    return;
  }

  if (eventType === 'score.updated') {
    applyScoreUpdated(lineups, payload, context);
  }
}

function applyDetailStreamEvents(lineups, streamEvents, context) {
  streamEvents.forEach((streamEvent) => {
    const type = streamEvent?.eventType;
    if (
      type === 'action.recorded' ||
      type === 'card.issued' ||
      type === 'score.updated'
    ) {
      applyDetailStreamEvent(lineups, streamEvent, context);
    }
  });
}

function applyStreamEventsToLineups(lineups, streamEvents, baseContext) {
  const normalizedEvents = normalizeStreamEventsList(streamEvents);
  const context = {
    ...baseContext,
    streamTeamIdMap: buildStreamTeamIdToCardTeamIdMap(
      normalizedEvents,
      baseContext.homeTeamId,
      baseContext.awayTeamId,
    ),
  };

  normalizedEvents.forEach((streamEvent) => {
    if (streamEvent?.eventType === 'lineup.confirmed') {
      applyStructuralStreamEvent(lineups, streamEvent, context);
    }
  });

  applyDetailStreamEvents(lineups, normalizedEvents, context);

  normalizedEvents.forEach((streamEvent) => {
    if (streamEvent?.eventType === 'substitution.made') {
      applyStructuralStreamEvent(lineups, streamEvent, context);
    }
  });

  applyDetailStreamEvents(lineups, normalizedEvents, context);
}

/**
 * @param {object|null|undefined} response — cuerpo axios o `live-events-mock.json`.
 * @param {object} [options]
 * @param {object[]} [options.previousLineups] — `lineups` actuales del contexto (`view=all`).
 * @param {string} [options.homeTeamId]
 * @param {string} [options.awayTeamId]
 * @param {object[]} [options.matchCard]
 * @returns {object[]|null}
 */
export function mapLiveEventsToLineups(response, options = {}) {
  if (response == null) return null;

  const source = response?.data ?? response;
  const matchId = source?.matchId != null ? String(source.matchId) : null;
  const streamEvents = source?.events;
  const hasStream =
    matchId != null &&
    Array.isArray(streamEvents) &&
    streamEvents.length > 0;

  if (!hasStream) {
    const existing =
      response?.data?.lineups ??
      response?.lineups ??
      (Array.isArray(response) ? response : null);
    return Array.isArray(existing) && existing.length > 0 ? existing : null;
  }

  const card = options.matchCard?.[0];
  const md = card?.summary?.matchDetails;
  const homeTeamId =
    options.homeTeamId ?? md?.home?.team?.teamId ?? null;
  const awayTeamId =
    options.awayTeamId ?? md?.away?.team?.teamId ?? null;

  const previousLineups = options.previousLineups;
  if (!Array.isArray(previousLineups) || previousLineups.length === 0) {
    return null;
  }

  const normalizedPrevious = normalizeLineupsArray(
    previousLineups,
    homeTeamId,
    awayTeamId,
    matchId,
  );
  if (normalizedPrevious.length === 0) return null;

  const lineups = cloneLineups(normalizedPrevious);

  applyStreamEventsToLineups(lineups, streamEvents, {
    matchId,
    homeTeamId,
    awayTeamId,
  });

  return lineups;
}
